import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { useState } from 'react';
import { generatePath } from 'react-router-dom';

import { useTypedParams } from '@sorare/routing';

import { Sport } from '@sorare/core/src/__generated__/globalTypes';
import { LoadingIndicator } from '@sorare/core/src/atoms/loader/LoadingIndicator';
import {
  ANY_SPORT_MY_CLUB_SLUG_CARDS,
  MLB_MY_CLUB_SLUG,
} from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useQuery } from '@sorare/core/src/hooks/graphql/useQuery';
import { isType } from '@sorare/core/src/lib/gql';

import { DeckEditorDialog } from '@sorare/marketplace/src/components/decks/DeckEditorDialog';

import { Aside } from '@sorare/gameplay/src/components/profile/Aside';
import { ClubLayout } from '@sorare/gameplay/src/components/profile/ClubLayout';
import { CurrentFixture } from '@sorare/gameplay/src/components/profile/CurrentFixture';
import {
  Gallery,
  MAX_CARDS_TO_DISPLAY,
} from '@sorare/gameplay/src/components/profile/Gallery';
import { ManagerIDHeader } from '@sorare/gameplay/src/components/profile/ManagerIDHeader';
import { RecentPurchases } from '@sorare/gameplay/src/components/profile/RecentPurchases';
import { WatchlistsBlock } from '@sorare/gameplay/src/components/profile/WatchlistsBlock';
import {
  Grid,
  Main,
  NewGrid,
} from '@sorare/gameplay/src/components/profile/ui';

import type {
  BaseballMyClubQuery,
  BaseballMyClubQueryVariables,
} from './__generated__/page.graphql';
import type { PageParams } from './__generated__/routeParams';

const BASEBALL_MY_CLUB_QUERY = gql`
  query BaseballMyClubQuery(
    $user: String!
    $firstCardsToDisplay: Int!
    $sport: Sport!
  ) {
    so5 {
      so5Fixture(sport: $sport, type: LIVE, eventType: CLASSIC) {
        slug
        ...CurrentFixture_so5Fixture
      }
    }
    user(slug: $user) {
      slug
      id
      trades(sport: [$sport], first: 20) {
        nodes {
          ...RecentPurchases_tokenOperationTradeInterface
        }
      }
      baseballUserProfile {
        id
        bannerUrl
        clubName
        shieldUrl
        socialPictureFingerprint
        highlightedDeck {
          id
          slug
          name
          ...Gallery_deck
        }
        ...ManagerIDHeader_userSportProfile
      }
      cards(
        sport: BASEBALL
        first: $firstCardsToDisplay
        rarities: [unique, super_rare, rare, limited, common]
        sorts: [RARITIES]
      ) {
        nodes {
          slug
          ...Gallery_anyCard
        }
      }
      cardCollections(startedOnly: true, first: 10, sport: BASEBALL) {
        nodes {
          id
          slug
          userCardCollection(forUserSlug: $user) {
            id
          }
        }
        totalCount
      }
      ...WatchlistsBlock_user
      ...ManagerIDHeader_user
      ...Gallery_publicUserInfoInterface
      ...CurrentFixture_user
    }
  }
  ${Gallery.fragments.deck}
  ${Gallery.fragments.anyCard}
  ${Gallery.fragments.publicUserInfoInterface}
  ${ManagerIDHeader.fragments.user}
  ${ManagerIDHeader.fragments.userSportProfile}
  ${CurrentFixture.fragments.so5Fixture}
  ${CurrentFixture.fragments.user}
  ${RecentPurchases.fragments.tokenOperationTradeInterface}
  ${WatchlistsBlock.fragments.user}
` as TypedDocumentNode<BaseballMyClubQuery, BaseballMyClubQueryVariables>;

const MyClub = () => {
  const { slug } = useTypedParams<PageParams>();
  const { generateSportContextPath } = useSportContext();

  const { up: isLaptopAndAbove } = useScreenSize('laptop');
  const { data, loading } = useQuery(BASEBALL_MY_CLUB_QUERY, {
    variables: {
      user: slug,
      firstCardsToDisplay: MAX_CARDS_TO_DISPLAY,
      sport: Sport.BASEBALL,
    },
  });
  const [editingListSlug, setEditingListSlug] = useState<string | null>();

  if (loading || !data) {
    return <LoadingIndicator fullHeight />;
  }

  const { user, so5 } = data;
  const { cards, trades } = user;

  const shareProps = {
    managerProfilePath: generatePath(MLB_MY_CLUB_SLUG, { slug }),
    managerProfileSocialPictures:
      data?.user.baseballUserProfile?.managerProfileSocialPictureUrls,
    galleryPath: generateSportContextPath(ANY_SPORT_MY_CLUB_SLUG_CARDS, {
      params: { slug },
    }),
    gallerySocialPictures:
      data?.user.baseballUserProfile?.gallerySocialPictureUrls,
  };

  const filteredRecentPurchases = (trades.nodes || []).filter(
    transaction =>
      (isType(transaction, 'TokenAuction') ||
        isType(transaction, 'TokenPrimaryOffer')) &&
      !!transaction.anyCards?.length
  );

  const blocks = {
    header: (
      <ManagerIDHeader
        user={user}
        sport={Sport.BASEBALL}
        userSportProfile={data.user.baseballUserProfile}
        {...shareProps}
      />
    ),
    currentFixture: (
      <CurrentFixture
        so5Fixture={so5.so5Fixture}
        sport={Sport.BASEBALL}
        user={user}
      />
    ),
    recentPurchases: (
      <RecentPurchases
        userSlug={user.slug}
        transactions={filteredRecentPurchases.slice(0, 5)}
      />
    ),
    watchlists: <WatchlistsBlock user={user} />,
    gallery: (
      <>
        <Gallery
          cards={cards.nodes}
          sport={Sport.BASEBALL}
          onAddDeck={() => setEditingListSlug(null)}
          onEditDeck={deckSlug => setEditingListSlug(deckSlug)}
          user={data.user}
        />
        {editingListSlug !== undefined && (
          <DeckEditorDialog
            sport={Sport.BASEBALL}
            slug={editingListSlug || ''}
            defaultName="Hall Of Fame"
            onClose={() => setEditingListSlug(undefined)}
          />
        )}
      </>
    ),
  };

  if (!isLaptopAndAbove) {
    return (
      <ClubLayout home bannerUrl={data?.user.baseballUserProfile?.bannerUrl}>
        <Grid>
          {blocks.header}
          {blocks.currentFixture}
          {blocks.recentPurchases}
          {blocks.watchlists}
          {blocks.gallery}
        </Grid>
      </ClubLayout>
    );
  }

  return (
    <ClubLayout home bannerUrl={data?.user.baseballUserProfile?.bannerUrl}>
      <NewGrid>
        <Main>
          {blocks.header}
          {blocks.gallery}
        </Main>
        <Aside>
          {blocks.currentFixture}
          {blocks.recentPurchases}
          {blocks.watchlists}
        </Aside>
      </NewGrid>
    </ClubLayout>
  );
};

export default MyClub;
